import React, { useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Breadcrumb from "./Breadcrumb";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import SvgShikshaFlower from "../shiksha logo_flower_png.svg";

const StoriesDetail = ({ storiesData, currentLanguage }) => {
  const { storiesId } = useParams();
  const location = useLocation();

  // Find the specific story based on the `storiesId` parameter
  const stories = storiesData.find(
    (e) => e.path.split("/").pop() === storiesId
  );

  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo(0, 0);
  }, [location]);

  const uniqueTitles = new Set();

  // Slick carousel settings
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    responsive: [
      {
        breakpoint: 1024,
        settings: { slidesToShow: 3 },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  // Return early if `stories` is undefined
  if (!stories) {
    return (
      <div className="container mx-auto px-4 md:px-8 lg:px-12 xl:px-20 max-w-screen-xl mt-6">
        <h2 className="text-center text-xl font-bold text-red-500">
          Story not found.
        </h2>
        <p className="text-center">
          The story you are looking for does not exist or has been removed.
        </p>
        <Link
          to={`/${currentLanguage === "en" ? "" : "hi/"}`}
          className="block text-center text-blue-500 underline mt-4"
        >
          Go back to the stories page
        </Link>
      </div>
    );
  }

  return (
    <div className="relative z-0">
      <div className="relative">
        {stories.commonBanner?.mediaImage2 && (
          <img
            src={stories.commonBanner.mediaImage2.url}
            className="w-full object-cover lg:mt-[48px] h-[175px]"
            alt={stories.commonBanner.mediaImage2.alt}
          />
        )}
        {/* Breadcrumb overlay on the banner */}
        <div className="absolute bottom-0 left-0 right-0 bg-[rgba(0,0,0,0.3)] p-2 sm:p-4">
          <Breadcrumb /> {/* Render the Breadcrumb component here */}
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 lg:px-12 xl:px-20 max-w-screen-xl mt-6">
        <div className="flex flex-col sm:flex-row items-center mb-[35px]">
          <div className="right-border-around relative w-full sm:w-[250px] sm:mr-[40px] pt-15 pr-15 pb-0 pl-0 mb-4 sm:mb-0">
            {stories.imageAbout.url && (
              <img
                src={stories.imageAbout.url}
                alt={stories.imageAbout.alt}
                className="relative w-full z-10"
              />
            )}
          </div>
          <Helmet>
            <title>{stories.title}</title>
          </Helmet>
          <h1 className="text-[15px] font-bold font-heading text-center sm:text-left">
            <strong className="block font-medium text-[22px] mb-[10px]">
              {stories.title}
            </strong>
          </h1>
        </div>
        <div
          className="mb-10 font-body text-justify"
          dangerouslySetInnerHTML={{ __html: stories.body?.value || "" }}
        />
      </div>

      <div className="bg-gray-300 pb-8">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 xl:px-20 max-w-screen-xl pt-5">
          <h2 className="text-xl sm:text-xl lg:text-2xl font-heading font-bold uppercase relative text-indian-red text-center section-heading">
            MORE STORIES AND TESTIMONIALS
            <div className="relative flex items-center justify-center line-with-image">
              <img
                src="/assets/SHIKSHA_Flower_SVG.svg"
                alt="headingflower"
                className="h-6 sm:h-6 lg:h-8 relative z-10 bg-gray-300 p-1 sm:p-2"
              />
            </div>
          </h2>
          <Slider {...sliderSettings} className="mt-[45px]">
            {storiesData
              .filter(
                (story) =>
                  story.path.split("/").pop() !== storiesId &&
                  !uniqueTitles.has(story.title) &&
                  uniqueTitles.add(story.title)
              )
              .map((story) => (
                <div key={story.id} className="item">
                  <Link
                    className="group block relative text-center overflow-hidden"
                    to={`/${
                      currentLanguage === "en" ? "" : "hi/"
                    }stories-of-change/${story.path.split("/").pop()}`}
                  >
                    {/* Card Layout */}
                    <div className="max-w-sm bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700">
                      <div className="relative w-full h-[200px] overflow-hidden rounded-t-lg bg-gray-200">
                        <img
                          src={story.imageAbout.url}
                          alt={story.imageAbout.alt}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-5">
                        <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white line-clamp-1">
                          {story.title}
                        </h5>
                        <a
                          href={`/${
                            currentLanguage === "en" ? "" : "hi/"
                          }stories-of-change/${story.path.split("/").pop()}`}
                          className="inline-flex items-center px-3 py-2 text-sm font-medium text-center text-white bg-warm-orange rounded-lg hover:underline"
                        >
                          Read more
                          <svg
                            className="w-3.5 h-3.5 ml-2"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 14 10"
                          >
                            <path
                              stroke="currentColor"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M1 5h12m0 0L9 1m4 4L9 9"
                            />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
          </Slider>
        </div>
      </div>
    </div>
  );
};


export default StoriesDetail;
